import React, { useCallback, useMemo } from 'react';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import BaseQuestionProps from 'pages/Activities/ActivityAssignmentStep/StepQuestion/BaseQuestionProps';
import { QuestionChoice } from '__generated__/graphql';
import MultipleChoiceQuestion from './MultipleChoiceQuestion/MultipleChoiceQuestion';
import SingleChoiceQuestion from './SingleChoiceQuestion/SingleChoiceQuestion';
import AlertMessage from 'components/AlertMessage/AlertMessage';

type Props = BaseQuestionProps<QuestionChoice[]>;

const ChoiceQuestion = ({ question, onChange, value, readOnly }: Props) => {
  const isMultiple = question.isMultiple;

  const options: DropdownOption[] = useMemo(
    () => question.questionChoices?.map((choice) => ({ id: choice.id, name: choice.label })) || [],
    [question.questionChoices]
  );
  const selectedOptions: DropdownOption[] = useMemo(() => {
    const optionsValue = (value || [])
      .map((selectedOption) => options.find((option) => option.id === selectedOption.id))
      .filter((x): x is DropdownOption => !!x);

    return isMultiple ? optionsValue : optionsValue.slice(0, 1);
  }, [isMultiple, options, value]);

  const handleChange = useCallback(
    (options: DropdownOption[]) => {
      const selected = options
        .map((option) => question.questionChoices?.find((x) => x.id === option.id))
        .filter((x): x is QuestionChoice => !!x);

      onChange && onChange(selected);
    },
    [onChange, question.questionChoices]
  );

  const handleChangeSingleChoice = useCallback(
    (option?: DropdownOption) => {
      handleChange(option ? [option] : []);
    },
    [handleChange]
  );

  if (isMultiple) {
    return (
      <>
        <MultipleChoiceQuestion
          options={options}
          onChange={handleChange}
          value={selectedOptions}
          disabled={readOnly}
        />
      </>
    );
  }

  return (
    <>
      <SingleChoiceQuestion
        options={options}
        onChange={handleChangeSingleChoice}
        value={selectedOptions[0]}
        name={`question__${question.id}`}
        disabled={readOnly}
      />
    </>
  );
};

export default ChoiceQuestion;
